import DOMPurify from 'dompurify'
import { FC, ReactNode, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Balance, Comment, Delete, Edit, HelpOutline, Label, PushPin } from '@mui/icons-material'
import { Box, Card, IconButton, Stack, TableContainer, Tooltip, Typography } from '@mui/material'

import { GRTable, GRTableColumn, SortOrder, TableSortValues } from '../../../../../components/GRTable/GRTable'
import { GRTooltip } from '../../../../../components/GRTooltip/GRTooltip'
import { GameIcon } from '../../../../../features/game'
import { Analyst } from '../../../../../types/Analyst'
import { AnalystAvatar } from '../../../../components/AnalystAvatar/AnalystAvatar'
import { GameNameCell } from '../../../games/components/GameNameCell/GameNameCell'
import { ReviewStatusIndicator } from '../../../review/components/ReviewStatusIndicator/ReviewStatusIndicator'
import { getRowSubgenre } from '../../helpers/dashboardHelpers'
import {
  DashboardAction,
  DashboardActionHandler,
  DashboardColumn,
  DashboardGameRow,
  GameNotesMap,
  StoredSubgenres,
  freeRankUnranked,
} from '../../types/Dashboard'

type CustomProps = {
  analystsByAppId: Record<string, Analyst>
  ftueCounts: Record<string, number>
  notes: GameNotesMap
  storedSubgenres: StoredSubgenres
  actions: DashboardAction[]
  onAction: DashboardActionHandler
  onShowReleaseNotes?: (row: DashboardGameRow) => void
}

type DashboardGamesTableProps = {
  rows: DashboardGameRow[]
  columns: DashboardColumn[]
  actions: DashboardAction[]
  onAction: DashboardActionHandler
  analystsByAppId?: Record<string, Analyst>
  ftueCounts?: Record<string, number>
  notes?: GameNotesMap
  storedSubgenres?: StoredSubgenres
  onShowReleaseNotes?: (row: DashboardGameRow) => void
  // Sorted by rank, ascending, until another column is sorted; otherwise the rows' own order.
  sortByRank?: boolean
  isLoading?: boolean
  noRowsLabel: ReactNode
}

type Column = GRTableColumn<DashboardGameRow, CustomProps>

const noValue = '---'
// The table only fits a 1440px screen with narrow text columns, so a long single word breaks instead of running into the next column.
const wrappingTextCellProps = { align: 'left', sx: { overflowWrap: 'anywhere' } } as const
const empty = {}
const rankSortValue = (rank?: number) => rank ?? TableSortValues.AlwaysLast
const analystOf = (row: DashboardGameRow, customProps?: CustomProps) => (row.game.appId ? customProps?.analystsByAppId[String(row.game.appId)] : undefined)

const rankText = (rank?: number, unranked?: number) => (rank === undefined || rank === null || rank === unranked ? (unranked ? '-' : noValue) : rank)

const actionIcons: Record<DashboardAction, ReactNode> = {
  [DashboardAction.ViewAnalyses]: <Balance />,
  [DashboardAction.Edit]: <Edit />,
  [DashboardAction.VersionTags]: <Label />,
  [DashboardAction.Delete]: <Delete />,
  [DashboardAction.Unpin]: <PushPin />,
}

const actionLabelKeys: Record<DashboardAction, string> = {
  [DashboardAction.ViewAnalyses]: 'internal-analysts:games_view_analyses',
  [DashboardAction.Edit]: 'internal-analysts:games_edit',
  [DashboardAction.VersionTags]: 'internal-analysts:games_edit_version_tags',
  [DashboardAction.Delete]: 'internal-analysts:games_delete',
  [DashboardAction.Unpin]: 'internal-analysts:games_unpin',
}

const isActionEnabled = (action: DashboardAction, row: DashboardGameRow) => (action === DashboardAction.VersionTags ? !!row.game.appId : !!row.game.id)

export const DashboardGamesTable: FC<DashboardGamesTableProps> = ({
  rows,
  columns: columnIds,
  actions,
  onAction,
  analystsByAppId = empty,
  ftueCounts = empty,
  notes = empty,
  storedSubgenres = empty,
  onShowReleaseNotes,
  sortByRank,
  isLoading,
  noRowsLabel,
}) => {
  const { t } = useTranslation()

  const customProps = useMemo<CustomProps>(
    () => ({ analystsByAppId, ftueCounts, notes, storedSubgenres, actions, onAction, onShowReleaseNotes }),
    [actions, analystsByAppId, ftueCounts, notes, onAction, onShowReleaseNotes, storedSubgenres]
  )

  const tableColumns = useMemo(() => {
    const definitions: Record<DashboardColumn, Column> = {
      [DashboardColumn.Analyst]: {
        labelAccessor: t('internal-common:analyst'),
        accessor: ({ row, customTableProps }) => <AnalystAvatar analyst={analystOf(row, customTableProps)} />,
        sortable: true,
        sortAccessor: ({ row, customTableProps }) => analystOf(row, customTableProps)?.name ?? '',
        headerCellProps: { sx: { width: 80, minWidth: 80 } },
      },
      [DashboardColumn.Rank]: {
        labelAccessor: t('internal-analysts:dashboard_rank'),
        accessor: ({ row }) => rankText(row.rank),
        sortable: true,
        sortAccessor: ({ row }) => rankSortValue(row.rank),
        defaultSortOrder: SortOrder.ASC,
        sortOrder: sortByRank ? SortOrder.ASC : undefined,
        headerCellProps: { sx: { width: 90 } },
      },
      [DashboardColumn.FreeRank]: {
        labelAccessor: t('internal-analysts:dashboard_free_rank'),
        accessor: ({ row }) => rankText(row.freeRank, freeRankUnranked),
        sortable: true,
        sortAccessor: ({ row }) => rankSortValue(row.freeRank),
        defaultSortOrder: SortOrder.ASC,
        headerCellProps: { sx: { width: 90 } },
      },
      [DashboardColumn.Icon]: {
        labelAccessor: t('internal-common:icon'),
        accessor: ({ row }) => <GameIcon src={row.game.getIcon() as string} gameName={row.game.name ?? ''} size="small" />,
        headerCellProps: { sx: { width: 62, minWidth: 62 } },
      },
      [DashboardColumn.Name]: {
        labelAccessor: t('internal-common:name'),
        accessor: ({ row }) => (row.game.id ? <GameNameCell game={row.game} /> : <Typography fontWeight="bold">{row.game.name || noValue}</Typography>),
        sortable: true,
        sortAccessor: ({ row }) => (row.game.name ?? '').toLowerCase(),
        cellProps: wrappingTextCellProps,
        headerCellProps: { align: 'left', sx: { minWidth: 130 } },
      },
      [DashboardColumn.Publisher]: {
        labelAccessor: t('internal-common:publisher'),
        accessor: ({ row }) => row.game.artist || noValue,
        sortable: true,
        sortAccessor: ({ row }) => (row.game.artist ?? '').toLowerCase(),
        cellProps: wrappingTextCellProps,
        headerCellProps: { align: 'left', sx: { minWidth: 100 } },
      },
      [DashboardColumn.Subgenre]: {
        labelAccessor: t('internal-analysts:games_subgenre'),
        accessor: ({ row, customTableProps }) => getRowSubgenre(row, customTableProps?.storedSubgenres ?? empty) || noValue,
        sortable: true,
        sortAccessor: ({ row, customTableProps }) => getRowSubgenre(row, customTableProps?.storedSubgenres ?? empty).toLowerCase(),
        cellProps: wrappingTextCellProps,
        headerCellProps: { align: 'left', sx: { minWidth: 100 } },
      },
      [DashboardColumn.Powerscore]: {
        labelAccessor: t('internal-common:powerscore'),
        accessor: ({ row }) => row.powerscore ?? noValue,
        sortable: true,
        sortAccessor: ({ row }) => row.powerscore ?? 0,
      },
      [DashboardColumn.Ftue]: {
        labelAccessor: t('internal-analysts:dashboard_ftue'),
        accessor: ({ row, customTableProps }) => (row.game.id && customTableProps?.ftueCounts[row.game.id]) || noValue,
      },
      [DashboardColumn.AnalystOverview]: {
        labelAccessor: t('internal-common:analyst_overview'),
        accessor: ({ row }) => (row.game.appId ? <ReviewStatusIndicator game={row.game} /> : noValue),
      },
      [DashboardColumn.DaysInTop200]: {
        labelAccessor: t('internal-analysts:dashboard_days_in_top_200'),
        accessor: ({ row }) => row.daysInTop200 ?? 0,
        sortable: true,
        sortAccessor: ({ row }) => row.daysInTop200 ?? 0,
        headerCellProps: { sx: { maxWidth: 140 } },
      },
      [DashboardColumn.FirstUnanalyzedVersion]: {
        labelAccessor: t('internal-analysts:dashboard_first_unanalyzed_version'),
        accessor: ({ row, customTableProps }) => {
          const version = row.firstUnanalyzedVersion?.version
          if (!version) return '-'
          const label = t('internal-analysts:dashboard_release_notes_show', { name: version })
          return (
            <Stack direction="row" alignItems="center" justifyContent="center" spacing={0.5}>
              <span>{version}</span>
              <Tooltip title={label}>
                <IconButton size="small" aria-label={label} onClick={() => customTableProps?.onShowReleaseNotes?.(row)}>
                  <HelpOutline fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>
          )
        },
        headerCellProps: { sx: { maxWidth: 140 } },
      },
    }

    const actionsColumn: Column = {
      labelAccessor: '',
      accessor: ({ row, customTableProps }) => {
        const note = row.game.id ? customTableProps?.notes[row.game.id]?.notes : undefined
        return (
          <Box display="flex" justifyContent="flex-end" alignItems="center">
            {note && (
              <GRTooltip content={<Box dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(note) }} />}>
                <IconButton size="small" color="success" aria-label={t('internal-analysts:dashboard_game_note')}>
                  <Comment fontSize="small" />
                </IconButton>
              </GRTooltip>
            )}
            {customTableProps?.actions.map((action) => {
              const label = t(actionLabelKeys[action])
              return (
                <Tooltip key={action} title={label}>
                  <span>
                    <IconButton
                      size="small"
                      aria-label={label}
                      color={action === DashboardAction.Delete ? 'error' : 'primary'}
                      disabled={!isActionEnabled(action, row)}
                      onClick={() => customTableProps.onAction(action, row)}
                    >
                      {actionIcons[action]}
                    </IconButton>
                  </span>
                </Tooltip>
              )
            })}
          </Box>
        )
      },
      cellProps: { align: 'right' },
      headerCellProps: { sx: { width: 34 * actions.length + 52, minWidth: 34 * actions.length + 52 } },
    }

    return [...columnIds.map((id) => definitions[id]), actionsColumn]
  }, [actions.length, columnIds, sortByRank, t])

  const [columns, setColumns] = useState(tableColumns)

  return (
    <TableContainer component={Card}>
      <GRTable
        rows={rows}
        columns={columns}
        onColumnsUpdated={setColumns}
        rowIdKey="key"
        customProps={customProps}
        noRowsLabel={noRowsLabel}
        isLoading={isLoading}
        striped
        hoverable
      />
    </TableContainer>
  )
}
